import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Building2, Upload, ArrowLeft } from "lucide-react";
import { BNTLogo } from "./layoutChrome";
import { setStoredClientId } from "@/lib/clientSelection";
import SecurityRecoveryQuestionFields from "@/components/SecurityRecoveryQuestionFields";
import { validateSecurityRecoveryRows } from "@/lib/commercialLicenseRequests";
import { formatRegimenSatSelectLabel, REGIMENES_SAT } from "@/lib/regimenesSat";

const DEFAULT_SECURITY_ROWS = [
  { q: "", answer: "" },
  { q: "", answer: "" },
  { q: "", answer: "" },
];

function normalizeRegimenCode(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  const m = s.match(/\b(60[1-9]|61[0-6]|62[0-6])\b/);
  return m ? m[1] : s.slice(0, 10);
}

export default function RegistroPublico() {
  const navigate = useNavigate();
  const { saasRegister } = useAuth();
  const [busy, setBusy] = useState(false);
  const [csfBusy, setCsfBusy] = useState(false);
  const [csfWarnings, setCsfWarnings] = useState([]);
  const [secRows, setSecRows] = useState(DEFAULT_SECURITY_ROWS);
  const [form, setForm] = useState({
    razon_social: "",
    rfc: "",
    nombre_comercial: "",
    domicilio: "",
    responsable: "",
    telefono: "",
    codigo_postal: "",
    regimen_fiscal: "",
    correo_facturacion: "",
    email: "",
    new_password: "",
    new_password2: "",
    new_portal_username: "",
    wants_own_accounting: true,
  });

  const onCsfPdfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCsfBusy(true);
    setCsfWarnings([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import-csf-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { parse_warnings, ...fields } = data;
      setForm((f) => ({
        ...f,
        rfc: fields.rfc || f.rfc,
        razon_social: fields.razon_social || f.razon_social,
        regimen_fiscal: normalizeRegimenCode(fields.regimen_fiscal) || f.regimen_fiscal,
        codigo_postal: fields.codigo_postal || f.codigo_postal,
        domicilio: fields.direccion || fields.domicilio || f.domicilio,
      }));
      setCsfWarnings(Array.isArray(parse_warnings) ? parse_warnings : []);
      toast.success("Datos leídos del PDF. Revise los campos antes de guardar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally {
      setCsfBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (form.new_password !== form.new_password2) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    if (!form.rfc.trim() || form.rfc.trim().length < 10) {
      toast.error("Indica un RFC válido");
      return;
    }
    const secErr = validateSecurityRecoveryRows(secRows);
    if (secErr) {
      toast.error(secErr);
      return;
    }
    setBusy(true);
    try {
      const data = await saasRegister({
        razon_social: form.razon_social.trim(),
        rfc: form.rfc.trim().toUpperCase(),
        nombre_comercial: form.nombre_comercial.trim(),
        domicilio: form.domicilio.trim(),
        responsable: form.responsable.trim(),
        telefono: form.telefono.trim(),
        codigo_postal: form.codigo_postal.trim(),
        regimen_fiscal: form.regimen_fiscal.trim(),
        correo_facturacion: form.correo_facturacion.trim(),
        email: form.email.trim().toLowerCase(),
        new_password: form.new_password,
        new_portal_username: form.new_portal_username.trim().toLowerCase().replace(/\s+/g, ""),
        security_questions: secRows.map((r) => ({ q: String(r.q || "").trim() })),
        security_answers: secRows.map((r) => String(r.answer || "").trim()),
        wants_own_accounting: Boolean(form.wants_own_accounting),
      });
      if (data?.preferred_client_id) {
        setStoredClientId(data.preferred_client_id);
      }
      toast.success("Cuenta creada. Periodo de prueba activo.");
      navigate("/", { replace: true });
    } catch {
      /* error en auth context */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F8] py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-sm shadow-sm p-8">
        <p className="text-center text-xs text-slate-600 mb-4">
          ¿Ya tiene cuenta?{" "}
          <Link to="/login" className="text-[#0F2D5C] font-medium underline inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" />
            Iniciar sesión
          </Link>
        </p>

        <div className="flex items-center gap-3 mb-6">
          <Building2 className="w-10 h-10 text-[#0F2D5C]" />
          <div>
            <h1 className="text-xl font-bold text-[#0F2D5C]">Crear cuenta</h1>
            <p className="text-xs text-slate-600 mt-1">
              Registro público con periodo de prueba. Complete sus datos fiscales y defina sus credenciales de acceso.
            </p>
            <p className="text-[11px] text-slate-600 mt-2 leading-snug" data-testid="registro-publico-orientation">
              El registro crea tu cuenta de comprador. Después podrás solicitar tu licencia web o generar una solicitud
              por PDF desde Configuración → Mi Negocio / Licencia.
            </p>
          </div>
        </div>
        <div className="mb-6 flex justify-center border-b border-slate-100 pb-6">
          <BNTLogo size="md" showDefault />
        </div>

        <form onSubmit={onSubmit} className="space-y-4 text-sm" data-testid="registro-publico-form">
          <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/80 p-4 space-y-2">
            <Label className="text-xs font-semibold text-[#0F2D5C]">Constancia de situación fiscal (opcional)</Label>
            <p className="text-[11px] text-slate-600 leading-snug">
              Suba el PDF del SAT para precargar RFC, razón social, régimen y domicilio. Revise los datos antes de guardar.
            </p>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-sm p-4 cursor-pointer hover:border-[#3DA873] hover:bg-emerald-50/30 transition-colors">
              {csfBusy ? (
                <Loader2 className="w-7 h-7 animate-spin text-[#0F2D5C]" />
              ) : (
                <Upload className="w-7 h-7 text-slate-400" />
              )}
              <span className="text-xs font-medium text-slate-700">
                {csfBusy ? "Leyendo PDF…" : "Seleccionar PDF de constancia"}
              </span>
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                disabled={csfBusy || busy}
                onChange={onCsfPdfSelected}
                data-testid="registro-publico-csf-file-input"
              />
            </label>
            {csfWarnings.length > 0 && (
              <ul className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 space-y-1 list-disc pl-4">
                {csfWarnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold">Razón social o nombre *</Label>
              <Input
                required
                value={form.razon_social}
                onChange={(e) => setForm((f) => ({ ...f, razon_social: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">RFC *</Label>
              <Input
                required
                value={form.rfc}
                onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value.toUpperCase() }))}
                className="mt-1 h-9 text-sm font-mono"
                maxLength={14}
              />
            </div>
            <div>
              <Label className="text-xs">Nombre comercial</Label>
              <Input
                value={form.nombre_comercial}
                onChange={(e) => setForm((f) => ({ ...f, nombre_comercial: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Domicilio fiscal</Label>
              <Input
                value={form.domicilio}
                onChange={(e) => setForm((f) => ({ ...f, domicilio: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Código postal</Label>
              <Input
                value={form.codigo_postal}
                onChange={(e) => setForm((f) => ({ ...f, codigo_postal: e.target.value }))}
                className="mt-1 h-9 text-sm font-mono"
                maxLength={5}
              />
            </div>
            <div>
              <Label className="text-xs">Régimen fiscal</Label>
              <Select
                value={form.regimen_fiscal || "__none__"}
                onValueChange={(v) => setForm((f) => ({ ...f, regimen_fiscal: v === "__none__" ? "" : v }))}
              >
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {REGIMENES_SAT.map((r) => (
                    <SelectItem key={r.c} value={r.c}>
                      {formatRegimenSatSelectLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Responsable / contacto</Label>
              <Input
                value={form.responsable}
                onChange={(e) => setForm((f) => ({ ...f, responsable: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input
                value={form.telefono}
                onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Correo de facturación</Label>
              <Input
                type="email"
                value={form.correo_facturacion}
                onChange={(e) => setForm((f) => ({ ...f, correo_facturacion: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Correo para acceso *</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="mt-1 h-9 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Usuario corto (opcional)</Label>
              <Input
                value={form.new_portal_username}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    new_portal_username: e.target.value.toLowerCase().replace(/\s+/g, ""),
                  }))
                }
                className="mt-1 h-9 text-xs font-mono"
                placeholder="ej. mi_despacho"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Contraseña *</Label>
              <Input
                required
                type="password"
                value={form.new_password}
                onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
                className="mt-1 h-9 text-sm"
                minLength={6}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Confirmar contraseña *</Label>
              <Input
                required
                type="password"
                value={form.new_password2}
                onChange={(e) => setForm((f) => ({ ...f, new_password2: e.target.value }))}
                className="mt-1 h-9 text-sm"
                minLength={6}
              />
            </div>
          </div>

          <label
            className="flex items-start gap-2 text-xs text-slate-700 border border-slate-100 rounded-sm p-3"
            data-testid="registro-publico-own-accounting"
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.wants_own_accounting}
              onChange={(e) => setForm((f) => ({ ...f, wants_own_accounting: e.target.checked }))}
              data-testid="registro-publico-own-accounting-checkbox"
            />
            <span>
              También llevaré la contabilidad de mi propio negocio en el sistema.
              <span className="block text-slate-500 mt-1">
                Déjelo marcado si además de administrar clientes también usará el sistema para su propio RFC.
              </span>
            </span>
          </label>

          <div className="pt-2 border-t border-slate-100 space-y-3" data-testid="registro-publico-security-section">
            <p className="text-xs font-semibold text-[#0F2D5C]">Preguntas de recuperación (3)</p>
            <p className="text-[11px] text-slate-600 m-0 leading-snug">
              Seleccione <strong>3 preguntas</strong> del catálogo y escriba una respuesta por cada una.
            </p>
            <SecurityRecoveryQuestionFields
              rows={secRows}
              onChange={setSecRows}
              disabled={busy}
              testIdPrefix="registro-publico-security"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-11 bg-[#0F2D5C] hover:bg-[#0a1f42] font-semibold"
            data-testid="registro-publico-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Guardar y continuar
          </Button>
        </form>
      </div>
    </div>
  );
}
